import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import PublicFormShell from '../PublicFormShell';
import { StyledSubmitButton } from '../PublicFormStyles';
import { useFormPalette } from '../DynamicFormPrimitives';
import {
  EmailMagicLinkGateScreen,
  AuthGateScreen,
  DisabledGateScreen,
  AccessDeniedScreen,
} from '../../../access/PublicAccessGateScreens';
import FormFlowTransitionScreen from './FormFlowTransitionScreen';
import FormFlowStepper from './FormFlowStepper';
import FormAuthenticatedUserChip from '../AuthenticatedUserChip';
import FormDraftStatusBar from '../FormDraftStatusBar';

/**
 * Respondent-facing layout for a Form Flow session.
 *
 * Reuses {@code PublicFormShell} verbatim (same skeleton, same cover, same submitted
 * affordance, same {@code DynamicFormRenderer} with {@code publicMode=true}) and slots
 * the flow-specific chrome in via:
 *
 *   topBar         → stepper + branded flow title/logo
 *   actions        → the big submit button
 *   authGate       → gate screen when {@code gateState} is set
 *   statusBanner   → terminal error message on submit failure
 *
 * Terminal/transient states that don't fit the public-form shell are rendered as
 * full-page takeovers before the shell:
 *
 *   TRANSITIONING  → {@link FormFlowTransitionScreen} (SSE-driven with manual retry)
 *   ABANDONED      → reuse {@link AccessDeniedScreen} styling with a "session ended" message
 *
 * {@code SUBMITTED} flows through {@code PublicFormShell}'s built-in success card.
 */
export default function FormFlowShell({
  session,
  answers,
  validation,
  loading,
  error,
  submitState,
  hasPendingUploadAnswers = false,
  completion,
  sseError = false,
  gateState = null,
  onRequestMagicLink,
  onSignedIn,
  onChangeAnswer,
  onFieldBlur,
  onUpload,
  onRemoveFile,
  retryUpload,
  autoSaveStatus = null,
  lastSavedAt = null,
  savingDraft = false,
  saveDraft = null,
  onSubmit,
  onBack,
  onRetryTransitioning,
}) {
  const palette = useFormPalette();
  const theme = useTheme();

  const authGate = useMemo(() => {
    if (gateState === 'disabled') return <DisabledGateScreen />;
    if (gateState === 'access_denied') {
      return (
        <AccessDeniedScreen
          title="Access denied"
          message="You do not have permission to continue this session."
        />
      );
    }
    if (gateState === 'email_required') {
      return <EmailMagicLinkGateScreen onRequestMagicLink={onRequestMagicLink} />;
    }
    if (gateState === 'auth_required') {
      return <AuthGateScreen onSignedIn={onSignedIn} />;
    }
    return null;
  }, [gateState, onRequestMagicLink, onSignedIn]);

  // Full-page takeover for transitioning — must run before PublicFormShell so the
  // stepper/submit chrome doesn't flash between steps.
  if (!authGate && (session?.status === 'TRANSITIONING' || submitState === 'transitioning')) {
    const accent = session?.flowConfig?.branding?.accentColor || theme.palette.primary.main;
    return (
      <FormFlowTransitionScreen
        accent={accent}
        sseError={sseError}
        onRetry={onRetryTransitioning}
      />
    );
  }

  if (!authGate && session?.status === 'ABANDONED') {
    return (
      <AccessDeniedScreen
        title="Session ended"
        message="This form session was abandoned. If you were in the middle of filling it out, please restart from the beginning."
      />
    );
  }

  const flowConfig = session?.flowConfig || {};
  const allowBack = flowConfig.allowBack !== false;
  const accent = flowConfig?.branding?.accentColor || theme.palette.primary.main;
  const brandTitle = flowConfig.title || '';

  // Treat either the persisted FlowSession.status OR the in-flight submitState
  // as "we are done" for rendering and button-disabled purposes. Without the
  // submitState fallback there is a live window after a COMPLETION response but
  // before the refetched {@code /session} lands in which {@code session.status}
  // is still WAITING — that was the "submit does nothing, second click 404s"
  // path we used to hit, because the Submit button stayed clickable.
  const submitted =
    session?.status === 'SUBMITTED' || submitState === 'completed';
  const currentStep = session?.currentStep || {};

  // Shape the PublicFormShell "form" prop from the flow's current step. We keep the form
  // populated on SUBMITTED too so PublicFormShell's built-in success card actually renders
  // — its pre-render "Form not found" guard fires whenever {@code form == null}.
  const form = session ? {
    title: currentStep.title || brandTitle,
    description: currentStep.description,
    fields: currentStep.fields || [],
  } : null;

  // When no flow branding cover is set, PublicFormShell derives a per-form gradient
  // from formId (current step component or flow entry).
  const coverImageUrl = flowConfig?.branding?.coverImage || undefined;
  const coverFormId = currentStep.componentId ?? session?.flowEntryId ?? session?.id;
  const submitting = submitState === 'submitting';

  // Present a submit error inline (below the form, above/replacing the submit button) via
  // statusBanner. Keeps the form visible so the respondent can retry with edited answers.
  const submitError = submitState === 'error' && error ? (
    <Box sx={{
      p: 2, borderRadius: '10px',
      bgcolor: palette.errorBg,
      border: `1px solid ${palette.errorText}33`,
    }}>
      <Typography sx={{ fontSize: '14px', color: palette.errorText }}>
        {error?.message || 'Submission failed. Please try again.'}
      </Typography>
    </Box>
  ) : null;

  const stepper = session?.stepper && session.stepper.length > 0 ? (
    <FormFlowStepper
      steps={session.stepper}
      accent={accent}
      allowBack={allowBack}
      onBack={onBack}
    />
  ) : null;

  const topBar = stepper ? (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2,
      px: { xs: 2, sm: 4 }, py: 1.25,
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {stepper}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <FormAuthenticatedUserChip inline />
      </Box>
    </Box>
  ) : null;

  return (
    <PublicFormShell
      loading={loading}
      error={(error && submitState !== 'error') ? (error.message || '') : ''}
      form={form}
      answers={answers}
      validation={validation}
      submitted={submitted}
      submitting={submitting}
      coverImageUrl={coverImageUrl}
      formId={coverFormId}
      onChange={onChangeAnswer}
      onFieldBlur={onFieldBlur}
      onUpload={onUpload}
      onRemoveFile={onRemoveFile}
      retryUpload={retryUpload}
      authGate={authGate}
      topBar={topBar}
      stickyHeader={session && !submitted ? (
        <FormDraftStatusBar
          palette={palette}
          autoSaveStatus={autoSaveStatus}
          lastSavedAt={lastSavedAt}
          savingDraft={savingDraft}
          disabled={submitting || submitted}
          onSaveDraft={saveDraft}
        />
      ) : null}
      statusBanner={submitError}
      actions={submitError ? null : (
        <StyledSubmitButton
          palette={palette}
          isLoading={submitting}
          disabled={submitting || hasPendingUploadAnswers || !session || submitted}
          onClick={onSubmit}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </StyledSubmitButton>
      )}
    />
  );
}
